import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Send, Mail, Phone, FileText, CheckCircle } from 'lucide-react';
import { sendContactEmail } from '../features/contact/services/emailService';

const formSchema = z.object({
  email: z.string().email('올바른 이메일 주소를 입력해주세요'),
  phone: z.string().optional(),
  projectDescription: z.string().min(10, '프로젝트 설명을 10자 이상 입력해주세요'),
});

type FormData = z.infer<typeof formSchema>;

export default function InlineContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const result = await sendContactEmail({
        companyName: '빠른 문의',
        contactPerson: '미입력',
        email: data.email,
        phone: data.phone || '',
        projectType: '미정',
        budget: '',
        timeline: '',
        description: data.projectDescription,
      });

      if (result.success) {
        setSubmitStatus('success');
        reset();
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-xl mx-auto">
      <AnimatePresence mode="wait">
        {submitStatus === 'success' ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-10 text-center"
          >
            <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="w-7 h-7 text-emerald-500" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">문의가 전송되었습니다</h3>
            <p className="text-zinc-400 mb-6">30분 이내로 연락드리겠습니다.</p>
            <button
              onClick={() => setSubmitStatus(null)}
              className="text-sm text-zinc-500 hover:text-white transition-colors"
            >
              새로운 문의 작성
            </button>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            onSubmit={handleSubmit(onSubmit)}
            className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8 space-y-5"
          >
            {/* Email */}
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-zinc-300 mb-2">
                <Mail className="w-3.5 h-3.5 text-zinc-500" />
                이메일 <span className="text-red-400">*</span>
              </label>
              <input
                {...register('email')}
                type="email"
                placeholder="your@email.com"
                className={`w-full px-4 py-3 rounded-xl bg-zinc-950/80 border ${
                  errors.email ? 'border-red-500/50' : 'border-zinc-800/60'
                } text-zinc-200 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none transition-colors duration-200`}
              />
              {errors.email && (
                <p className="text-xs text-red-400 mt-1.5">{errors.email.message}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-zinc-300 mb-2">
                <Phone className="w-3.5 h-3.5 text-zinc-500" />
                연락처 <span className="text-zinc-600 text-xs">(선택)</span>
              </label>
              <input
                {...register('phone')}
                type="tel"
                placeholder="010-0000-0000"
                className="w-full px-4 py-3 rounded-xl bg-zinc-950/80 border border-zinc-800/60 text-zinc-200 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none transition-colors duration-200"
              />
            </div>

            {/* Project Description */}
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-zinc-300 mb-2">
                <FileText className="w-3.5 h-3.5 text-zinc-500" />
                프로젝트 설명 <span className="text-red-400">*</span>
              </label>
              <textarea
                {...register('projectDescription')}
                rows={4}
                placeholder="어떤 프로젝트를 구상하고 계신가요? 간단히 설명해주세요."
                className={`w-full px-4 py-3 rounded-xl bg-zinc-950/80 border ${
                  errors.projectDescription ? 'border-red-500/50' : 'border-zinc-800/60'
                } text-zinc-200 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none transition-colors duration-200 resize-none`}
              />
              {errors.projectDescription && (
                <p className="text-xs text-red-400 mt-1.5">{errors.projectDescription.message}</p>
              )}
            </div>

            {/* Submit */}
            <motion.button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3.5 rounded-xl bg-white text-black font-medium hover:bg-zinc-100 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              {isSubmitting ? (
                <>
                  <motion.div
                    className="w-4 h-4 border-2 border-zinc-400 border-t-black rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  />
                  전송 중...
                </>
              ) : (
                <>
                  프로젝트 문의하기
                  <Send className="w-4 h-4" />
                </>
              )}
            </motion.button>

            {/* Error message */}
            <AnimatePresence>
              {submitStatus === 'error' && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-sm text-red-400 text-center"
                >
                  전송 중 오류가 발생했습니다. 다시 시도해주세요.
                </motion.p>
              )}
            </AnimatePresence>

            {/* Kakao link */}
            <div className="text-center pt-2">
              <span className="text-xs text-zinc-600">또는 </span>
              <a
                href="http://pf.kakao.com/_XSxhNn/chat"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-[#FEE500] transition-colors"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 256 256" fill="currentColor">
                  <path d="M128 36C70.562 36 24 72.713 24 118c0 29.279 19.466 54.97 48.748 69.477l-10.534 38.883c-.905 3.34 2.998 5.99 5.756 3.905l46.725-33.452C120.077 197.905 125.975 198.5 128 198.5c57.438 0 104-34.213 104-80.5S185.438 36 128 36" />
                </svg>
                카카오톡으로 문의
              </a>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}
